import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send } from 'lucide-react';

const WHATSAPP_NUMBER = '919535621900';

const Enquiry = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    projectType: '',
    message: '',
  });

  const projectTypes = [
    'Residential',
    'Commercial',
    'Hospitality',
    'Outdoor/Landscape',
    'Other',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Compose WhatsApp message with form data
    const whatsappMessage = `Hi Tilerra,

I'd like to enquire about your premium tile collections.

*Enquiry Details:*
━━━━━━━━━━━━━━━━
📌 *Name:* ${formData.name}
📞 *Phone:* ${formData.phone}
🏠 *Project Type:* ${formData.projectType}
${formData.message ? `💬 *Message:* ${formData.message}` : ''}
━━━━━━━━━━━━━━━━

Looking forward to hearing from you!`;

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section
      id="enquiry"
      ref={sectionRef}
      data-testid="enquiry-section"
      className="py-24 md:py-32 lg:py-40 bg-stone-white"
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-burgundy font-medium">
              Get in Touch
            </span>
            <h2
              data-testid="enquiry-heading"
              className="font-playfair text-3xl md:text-4xl lg:text-5xl text-matte-charcoal mt-4 mb-6 leading-tight"
            >
              Let's Design
              <br />
              Your Space
            </h2>
            <p className="text-base md:text-lg text-stone-grey leading-relaxed mb-8 max-w-md">
              Visit our showroom to experience our curated collections, or reach
              out to discuss your project with our design consultants.
            </p>

            {/* Contact Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-burgundy/10 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-burgundy"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <a
                  href="tel:+919535621900"
                  data-testid="enquiry-phone"
                  className="text-matte-charcoal hover:text-burgundy transition-colors"
                >
                  +91 95356 21900
                </a>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-burgundy/10 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-burgundy"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <a
                  href="mailto:faisal@tilerra.in"
                  data-testid="enquiry-email"
                  className="text-matte-charcoal hover:text-burgundy transition-colors"
                >
                  faisal@tilerra.in
                </a>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
          >
            <form
              onSubmit={handleSubmit}
              data-testid="enquiry-form"
              className="space-y-6"
            >
              {/* Name */}
              <div className="relative">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="enquiry-name-input"
                  className="w-full bg-transparent border-b border-sand py-4 px-0 text-matte-charcoal placeholder-transparent focus:outline-none focus:border-burgundy transition-colors peer"
                  placeholder="Your Name"
                />
                <label className="absolute left-0 -top-2 text-xs uppercase tracking-[0.15em] text-stone-grey transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-focus:-top-2 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-[0.15em] peer-focus:text-burgundy">
                  Your Name
                </label>
              </div>

              {/* Phone */}
              <div className="relative">
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  data-testid="enquiry-phone-input"
                  className="w-full bg-transparent border-b border-sand py-4 px-0 text-matte-charcoal placeholder-transparent focus:outline-none focus:border-burgundy transition-colors peer"
                  placeholder="Phone Number"
                />
                <label className="absolute left-0 -top-2 text-xs uppercase tracking-[0.15em] text-stone-grey transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-focus:-top-2 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-[0.15em] peer-focus:text-burgundy">
                  Phone Number
                </label>
              </div>

              {/* Project Type */}
              <div className="relative">
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  data-testid="enquiry-project-type-select"
                  className="w-full bg-transparent border-b border-sand py-4 px-0 text-matte-charcoal focus:outline-none focus:border-burgundy transition-colors appearance-none cursor-pointer"
                >
                  <option value="" disabled>
                    Select Project Type
                  </option>
                  {projectTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <svg
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-grey pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {/* Message */}
              <div className="relative">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  data-testid="enquiry-message-input"
                  className="w-full bg-transparent border-b border-sand py-4 px-0 text-matte-charcoal placeholder-transparent focus:outline-none focus:border-burgundy transition-colors peer resize-none"
                  placeholder="Your Message"
                />
                <label className="absolute left-0 -top-2 text-xs uppercase tracking-[0.15em] text-stone-grey transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-focus:-top-2 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-[0.15em] peer-focus:text-burgundy">
                  Your Message (Optional)
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!formData.name || !formData.phone || !formData.projectType}
                data-testid="enquiry-submit-btn"
                className="group w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#25D366]/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Send via WhatsApp
                <Send className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Enquiry;
